import { useCallback, useMemo } from 'react';
import { usePageContext } from 'vike-react/usePageContext';

import { menuHref } from '@/components/react-aria/menu';
import {
  Pagination,
  PaginationGap,
  PaginationList,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
} from '@/components/react-aria/pgagination';
import { request } from '@/renderer/utils/api';
import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import type { UseInfiniteRequestResult } from '@/renderer/utils/api/types';
import useMe from '@/renderer/utils/useMe';

import { PlayList } from './PlayRow';
import type { SortMode } from './ResultsControls';
import { useReconcileResults, useRegisterReconcile } from './ResultsReconcileContext';
import { derivePager } from './pagination';

type ReleaseResult = 'pending' | 'win' | 'loss' | 'push' | 'canceled' | 'noAction';

// The sort toggle maps to the request order + the date-group key: game time (kickoff)
// or release time (when we posted it — "what did I miss in the last release").
//
// The two feeds sort on different columns: the archive reads `releases` directly, while
// a member reads `messageReceipts` and has to reach the release's timestamps through the
// nested release relation (dot-path). Both feeds sort on the SAME value the date headers
// are grouped by, so a header can never be emitted out of sequence (#2079) — which is
// also why the receipt feed orders on `release.createdAt` rather than the receipt's own
// createdAt.
const orderBy = (sort: SortMode) => (sort === 'release' ? 'createdAt' : 'gameTime');
const receiptOrderBy = (sort: SortMode) =>
  sort === 'release' ? 'release.createdAt' : 'release.gameTime';
const dateKey = (sort: SortMode): 'gameTime' | 'createdAt' =>
  sort === 'release' ? 'createdAt' : 'gameTime';

// Graded results read newest-first. The direction is explicit rather than left to the
// request layer's default: both sort keys are heavily tied (a Saturday slate shares one
// kickoff), and the server only composes a total order — the key plus `id` as the
// tiebreaker — for the direction it is given. Without one, the pages of this
// offset-paginated list would not compose into one ordered list (#2079).
const RESULTS_DIR = 'DESC' as const;

// Graded (resolved) results — the redesign's "Results" section is graded-only; pending
// plays live in their own section above (#2004).
const GRADED_RESULTS: ReleaseResult[] = ['win', 'loss', 'push', 'canceled', 'noAction'];

const playTypeFilter = (
  playType?: API.PlayTypes | API.PlayTypes[] | 'full' | 'st' | 'hp' | 'pf',
) =>
  playType === 'full'
    ? ['side', 'total', 'prop', 'future']
    : playType === 'hp'
      ? ['side', 'total']
      : playType === 'st'
        ? ['side', 'total']
        : playType === 'pf'
          ? ['prop', 'future']
          : playType && typeof playType === 'string'
            ? [playType]
            : playType || ['side', 'total', 'prop', 'future'];

export default function ResultsTable<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  sportId,
  serviceIds,
  playType,
  gameType,
  startDate,
  endDate,
  userId,
  free,
  result,
  emptyState,
  sort = 'game',
}: {
  sportId?: number | number[];
  serviceIds?: number[];
  playType?: API.PlayTypes | API.PlayTypes[] | 'full' | 'st' | 'hp' | 'pf';
  gameType?: API.GameTypes[];
  startDate?: Date;
  endDate?: Date;
  userId: UID;
  free?: boolean;
  // Optional result filter (e.g. graded-only for the Results section, since pending
  // plays render in their own section above).
  result?: ReleaseResult | ReleaseResult[];
  emptyState?: React.ReactNode;
  sort?: SortMode;
}) {
  const {
    urlParsed: { search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const eventValidate = useCallback(
    async (
      _event: MessageEvent,
      ojson: API.SubscriptionResponse<API.MessageReceipt> | API.WebsocketResult<API.Id>,
    ) => {
      const data = ojson.data as API.MessageReceipt;

      if (!data.releaseId) {
        return false;
      }

      if (ojson.message === 'messageReceipt/updated') {
        return {
          ...ojson,
          data: (
            await request<API.WithData<API.MessageReceipt>>(
              `/api/v1/messageReceipt/${userId}/${ojson.data as number}`,
            )
          ).data,
        } satisfies API.SubscriptionResponse<API.MessageReceipt>;
      }
    },
    [userId],
  );

  const releasesRequest = useNextInfiniteRequest<
    API.Release,
    void,
    // `withMarketLine` opts the archive list into the current-market-line decoration
    // on pending picks (#1848) — a top-level query flag alongside page/order/data.
    API.WithPageInput<API.Release> & { withMarketLine?: boolean }
  >(
    useMemo(
      () => ({
        key: 'fetchReleases',
        req: `/api/v1/release`,
        query: {
          order: {
            by: orderBy(sort),
            dir: RESULTS_DIR,
          },
          page: {
            limit,
            offset: limit * (page - 1),
          },
          withMarketLine: true,
          data: {
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            playType: playTypeFilter(playType),
            result,
            gameType,
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId !== 'archive',
      }),
      [
        limit,
        page,
        userId,
        sportId,
        serviceIds,
        playType,
        gameType,
        startDate,
        endDate,
        free,
        result,
        sort,
      ],
    ),
  );

  const messageReceiptsRequest = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'fetchMessageReleases',
        req: `/api/v1/messageReceipt/${userId}`,
        event: 'messageReceipt',
        eventValidate,
        query: {
          order: { by: receiptOrderBy(sort), dir: RESULTS_DIR },
          page: {
            limit,
            offset: limit * (page - 1),
          },
          data: {
            type: 'release',
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            result,
            playType: playTypeFilter(playType),
            gameType,
            startDate,
            endDate,
            free,
          },
        },
        skipPoll: true,
        pause: userId === 'archive',
      }),
      [
        limit,
        page,
        userId,
        eventValidate,
        sportId,
        serviceIds,
        playType,
        gameType,
        startDate,
        endDate,
        free,
        result,
        sort,
      ],
    ),
  );

  return (
    <ResultsTableContent
      userId={userId}
      infiniteRequest={userId === 'archive' ? releasesRequest : messageReceiptsRequest}
      emptyState={emptyState}
      sort={sort}
    />
  );
}

const ALLOWED_LIMITS = [1, 5, 10, 25, 50, 100];
const DEFAULT_LIMIT = 50;

/**
 * Renders the play-row list (via PlayList) for a results request, plus pagination.
 * Both the member (`me`) and public (`archive`) views funnel through here, and a grade
 * in either the pending or graded list reconciles the sibling live (#1284).
 */
export function ResultsTableContent<
  const UID extends API.Id | 'me' | 'archive',
  const _ResponseType extends UID extends 'archive' ? API.Release : API.MessageReceipt,
>({
  infiniteRequest,
  hidePagination,
  emptyState,
  sort = 'game',
}: {
  // Retained for call-site typing; the row shape is detected per row, not from the
  // viewer, so this component no longer branches on it.
  userId: UID;
  infiniteRequest:
    | UseInfiniteRequestResult<API.Release, void>
    | UseInfiniteRequestResult<API.MessageReceipt, void>
    | null;
  hidePagination?: boolean;
  emptyState?: React.ReactNode;
  sort?: SortMode;
}) {
  const user = useMe();
  const isAdmin = user?.role === 'admin';

  // Register this list's refetch and grab the reconcile-all so a grade here (or in the
  // sibling pending list) refreshes BOTH lists live (#1284).
  useRegisterReconcile(infiniteRequest?.refetch);
  const reconcileResults = useReconcileResults();

  const {
    isPending,
    error,
    data: results,
    isLoading,
  } = infiniteRequest || {
    isPending: true,
    error: null,
    data: null,
  };

  const rows = results?.data || [];

  const {
    urlParsed: { pathname, search },
  } = usePageContext();

  const limit = ALLOWED_LIMITS.includes(+search?.limit) ? +search?.limit : DEFAULT_LIMIT;
  const page = +search?.page || 1;

  const perPage = results?.pageInfo.limit || limit || results?.pageInfo.total || 1;
  // The member feed has no `total` (the COUNT is skipped on purpose, #2059), so the
  // pager falls back to an exact Prev/Next pair off `hasNextPage`; the archive feed
  // still reports a total and keeps the numbered pager. See ./pagination (#2084).
  const pager = derivePager({
    page,
    perPage,
    total: results?.pageInfo.total,
    hasNextPage: results?.pageInfo.hasNextPage,
  });
  const { pageCount } = pager;

  return (
    <>
      <PlayList
        rows={rows}
        isAdmin={isAdmin}
        isPending={isPending}
        onGrade={reconcileResults ?? infiniteRequest?.refetch}
        emptyState={emptyState}
        dateBy={dateKey(sort)}
      />

      {!!error?.message && <div className="mt-2 text-sm text-rose-500">Error: {error.message}</div>}

      {!hidePagination && pager.mode !== 'none' && (
        <section className="mt-4 w-full select-none">
          <Pagination>
            <PaginationPrevious
              href={
                menuHref(search, {
                  page: pager.previousPage === 1 ? undefined : pager.previousPage,
                }) || pathname
              }
              isDisabled={!pager.hasPrevious}
            />
            <PaginationList>
              {/* Without a total the page count is unknowable, so show where the reader
                  is rather than a numbered list that cannot be built. */}
              {pager.mode === 'prevNext' && (
                <PaginationPage
                  href={menuHref(search, { page: page === 1 ? undefined : page }) || pathname}
                  current
                  isLoading={isLoading || isPending}
                >{`${page}`}</PaginationPage>
              )}
              {pager.mode === 'numbered' && (
                <>
                  {pageCount > 5 && page >= 4 && (
                    <PaginationPage href={menuHref(search, { page: undefined }) || pathname}>
                      1
                    </PaginationPage>
                  )}
                  {pageCount > 6 && page >= 5 && <PaginationGap />}
                  {new Array(pageCount < 5 ? pageCount : 5)
                    .fill(Math.max(1, Math.min(page - 2, pageCount - 4)))
                    .map((start, idx) => {
                      const npage = start + idx;
                      return (
                        <PaginationPage
                          key={npage}
                          href={
                            menuHref(search, { page: npage === 1 ? undefined : npage }) || pathname
                          }
                          current={page === npage}
                          isLoading={isLoading || isPending}
                        >{`${npage}`}</PaginationPage>
                      );
                    })}
                  {pageCount > 6 && pageCount - page - 2 >= 2 && <PaginationGap />}
                  {pageCount > 5 && pageCount - page - 2 >= 1 && (
                    <PaginationPage
                      href={
                        menuHref(search, { page: pageCount === 1 ? undefined : pageCount }) ||
                        pathname
                      }
                    >{`${pageCount}`}</PaginationPage>
                  )}
                </>
              )}
            </PaginationList>
            <PaginationNext
              href={
                menuHref(search, { page: pager.nextPage === 1 ? undefined : pager.nextPage }) ||
                pathname
              }
              isDisabled={!pager.hasNext}
            />
          </Pagination>
        </section>
      )}
    </>
  );
}

export { GRADED_RESULTS };
